import dayjs from 'dayjs';
import head from 'lodash/head';
import sortBy from 'lodash/sortBy';
import toNumber from 'lodash/toNumber';
import * as Sentry from '@sentry/capacitor';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileTransfer } from '@capacitor/file-transfer';
import { type PatientSessionDetails, type RecordingUploadRequest } from '~/api/generated';
import { FileContentTypeEnum, FileTypeEnum, RecordingSourceEnum } from '~/utils/constants/session';
import { deleteSessionFiles, getPhotosFromStorage, recordingContentType, UploadError } from '~/utils/file';
import { removeSessionStorage } from '~/utils/session';

type Photo = {
  file: Blob;
  original_file_name: string;
  size: number;
  session: PatientSessionDetails['id'];
};

type PreparedPayload = {
  url: string;
  path: string;
  file: Blob;
};

type UploadEntry = {
  sessionId: PatientSessionDetails['id'];
  sessionName: string;
  status: 'uploading' | 'error';
  errorMessageKey?: string;
  error?: unknown;
};

export const useRecordingUploadStore = defineStore('recording-upload', {
  state: () => ({
    uploads: {} as Record<PatientSessionDetails['id'], UploadEntry>,
    // Success signal consumed and cleared by the upload indicator.
    completed: null as { sessionId: PatientSessionDetails['id']; sessionName: string } | null
  }),
  getters: {
    activeUploads: (state) => Object.values(state.uploads).filter((upload) => upload.status === 'uploading'),
    erroredUploads: (state) => Object.values(state.uploads).filter((upload) => upload.status === 'error'),
    hasActiveUploads(): boolean {
      return this.activeUploads.length > 0;
    }
  },
  actions: {
    // Runs detached on native, so the toast cannot live here (no component
    // context): the error key is stored and the indicator surfaces it.
    async uploadSession({
      sessionId,
      sessionName,
      isNativePlatform
    }: {
      sessionId: PatientSessionDetails['id'];
      sessionName: string;
      isNativePlatform: boolean;
    }) {
      const config = useRuntimeConfig();
      const apiStore = useApiStore();
      const uploadStore = useUploadStore();
      const sessionStore = useSessionStore();
      const recordingStore = useRecordingStore();

      // If the api base is our default api base, we need to set the blob type header for Azure Blob Storage
      const blobHeaders = apiStore.apiBase === config.public.apiBase ? { 'x-ms-blob-type': 'BlockBlob' } : undefined;

      this.uploads[sessionId] = { sessionId, sessionName, status: 'uploading' };

      const handleProcessRecordingError = (error: unknown, errorMessageKey: string) => {
        Sentry.captureException(error);

        this.uploads[sessionId] = { sessionId, sessionName, status: 'error', errorMessageKey, error };

        throw new UploadError();
      };

      const getPreparedImagePayload = (files: Array<Photo>) =>
        Promise.all(
          files.map(({ file }) =>
            uploadStore
              .getSignedUrl({ requestBody: { content_type: FileContentTypeEnum.Jpg, file_type: FileTypeEnum.Photo } })
              .then(({ url, file_path: path }) => ({ url, path, file }))
          )
        );

      const processAndUploadRecordings = async (): Promise<Array<RecordingUploadRequest>> => {
        const dirPath = getSessionsDirectory(sessionId);
        const { files } = await Filesystem.readdir({ path: dirPath, directory: Directory.Documents });
        const filesSortedByTimestamp = sortBy(files, ({ name }) =>
          toNumber(head(name.split('.'))?.replace('recording-', ''))
        );

        const processedRecordings = [] as Array<RecordingUploadRequest>;

        for (const [sequenceNumber, { name, ctime, size }] of filesSortedByTimestamp.entries()) {
          const recordingPath = `${dirPath}/${name}`;
          const recordingMeta = recordingStore.getRecordingByPath(recordingPath);
          const { url, file_path: file } = await uploadStore.getSignedUrl({
            requestBody: {
              content_type: recordingMeta?.mimeType || recordingContentType,
              file_type: FileTypeEnum.Recording,
              recording_source: RecordingSourceEnum.App
            }
          });

          if (isNativePlatform) {
            const { uri: path } = await Filesystem.getUri({ directory: Directory.Documents, path: recordingPath });

            await FileTransfer.uploadFile({
              path,
              url,
              headers: blobHeaders,
              mimeType: recordingMeta?.mimeType || recordingContentType,
              method: 'PUT'
            });
          } else {
            const { data } = await Filesystem.readFile({ path: recordingPath, directory: Directory.Documents });

            await uploadStore.uploadFile({ url, body: data as Blob });
          }

          recordingStore.removeRecordingByPath(recordingPath);

          processedRecordings.push({
            file,
            started_at: recordingMeta?.msDuration
              ? dayjs(ctime).subtract(recordingMeta.msDuration, 'ms').toISOString()
              : dayjs(ctime).toISOString(),
            completed_at: dayjs(ctime).toISOString(),
            original_file_name: name,
            size,
            sequence_number: sequenceNumber
          });
        }

        return processedRecordings;
      };

      try {
        let photos: Array<Photo> = [];

        try {
          photos = await getPhotosFromStorage(sessionId);
        } catch (e) {
          handleProcessRecordingError(e, 'recordings.notifications.failed-to-get-photos-from-storage');
        }

        let imagePayload: PreparedPayload[] = [];

        try {
          imagePayload = await getPreparedImagePayload(photos);
        } catch (e) {
          handleProcessRecordingError(e, 'recordings.notifications.failed-to-prepare-image-payload');
        }

        try {
          await Promise.all(imagePayload.map(({ url, file: body }) => uploadStore.uploadFile({ url, body })));
        } catch (e) {
          handleProcessRecordingError(e, 'recordings.notifications.recording-uploading-failed');
        }

        try {
          await Promise.all(
            photos.map((file, index) =>
              sessionStore.createPhoto({
                sessionId,
                requestBody: { ...file, file: imagePayload[index].path, mime_type: FileContentTypeEnum.Jpg }
              })
            )
          );
        } catch (e) {
          handleProcessRecordingError(e, 'recordings.notifications.photo-uploading-failed');
        }

        try {
          const recordingPayload = await processAndUploadRecordings();

          await sessionStore.createRecording({ sessionId, requestBody: recordingPayload });
        } catch (e) {
          handleProcessRecordingError(e, 'recordings.notifications.recording-create-failed');
        }
      } catch (error) {
        if (!(error instanceof UploadError)) {
          Sentry.captureException(error);

          this.uploads[sessionId] = {
            sessionId,
            sessionName,
            status: 'error',
            errorMessageKey: 'recordings.notifications.recording-processing-failed',
            error
          };
        }

        return false;
      }

      try {
        await deleteSessionFiles(sessionId);

        removeSessionStorage(sessionId);

        // The index list only needs refreshing on native, where we navigated away;
        // web refetches the open session itself
        if (isNativePlatform) await sessionStore.getSessions();
      } catch (error) {
        Sentry.captureException(error);
      }

      delete this.uploads[sessionId];

      this.completed = { sessionId, sessionName };

      return true;
    },

    clearUpload(sessionId: PatientSessionDetails['id']) {
      delete this.uploads[sessionId];
    },

    clearCompleted() {
      this.completed = null;
    }
  }
});
